import {
  generateKeypair,
  importPrivateKey,
  signEnvelope,
  randomNonceB64,
} from '../../shared/sign';
import {
  canonicalEnvelope,
  canonicalSessionStart,
  canonicalSessionStop,
  canonicalShareMint,
  canonicalDeviceRead,
} from '../../shared/crypto';
import {
  parseDeviceCreated,
  parsePingAccepted,
  parseDevicePings,
  parseSessionStarted,
  parseSessionStopped,
  parseShareMinted,
  parseShareMeta,
  parseSharePings,
  parseGroupRecent,
} from '../../shared/wire';
import type { Ping } from '../../shared/wire';
import { SHORT } from '../../shared/token';
import {
  saveKeypair, loadKeypair, clearKeypair,
  loadDeviceHistory, saveDeviceHistory,
  loadStoredToken, saveToken,
  isPendingDevice, setPendingFlag, retokenHistory,
} from '../storage/persistence';
import { MOCK } from '../state/device';

// Inverse of shared/token SHORT (key→short) → short→key lookup for decodeToken.
const byShort: Record<string, string> = Object.fromEntries(
  Object.entries(SHORT).map(([k, v]) => [v, k]),
);

export async function apiCreateDevice(animals: string[], group: string[] | null, invite?: string){
  // Generate the keypair before the POST so the server can store the pubkey
  // alongside the device row. The private key never leaves this browser.
  const keypair = await generateKeypair();
  // `invite` is sent only when present. The server ignores it for an unclaimed
  // group and requires it for a claimed one (ADR 0025) — so an absent invite is
  // safe for every legacy caller (the picker, #mint-private).
  const body: { animals: string[]; group: string[]; pubkey: string; invite?: string } =
    { animals, group: group || [], pubkey: keypair.pub };
  if(invite) body.invite = invite;
  const res = await fetch('/api/v1/devices', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if(!res.ok) throw new Error('create-device failed: ' + res.status);
  const out = parseDeviceCreated(await res.json());
  saveKeypair(out.token, keypair);
  return out;
}

export async function apiPostPings(token: string, pings: Ping[]){
  const stored = loadKeypair(token);
  if(!stored){
    const err: any = new Error('no signing key for ' + token);
    err.code = 'no-key';
    throw err;
  }
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const envelope = canonicalEnvelope(token, pings, timestamp, nonce);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch('/api/v1/pings', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'X-Trail-Signature': sig,
    },
    body: JSON.stringify({ token, pings, timestamp, nonce }),
  });
  if(!res.ok){
    const err: any = new Error('post-pings failed: ' + res.status);
    err.status = res.status;
    // Best-effort capture of the server's error message so callers
    // (e.g. the stale-session self-heal in tickRecording) can branch on it.
    try { err.body = await res.json(); } catch { /* ignore */ }
    throw err;
  }
  return parsePingAccepted(await res.json());
}

export async function apiGetPings(token: string){
  // Self-rehydrate read. Always SIGN when we hold the device key: a public
  // device still serves unsigned server-side, but a non-public (owned) device
  // 404s without a valid owner signature (ADR 0015). The owner holds the key,
  // so signing makes the self-read work regardless of visibility. No `?since`,
  // so the signed `since` is null — it must match the server's parsed value.
  const stored = loadKeypair(token);
  let headers: Record<string, string> | undefined;
  if(stored){
    try {
      const privKey = await importPrivateKey(stored.priv);
      const timestamp = Date.now();
      const nonce = randomNonceB64();
      const sig = await signEnvelope(privKey, canonicalDeviceRead(token, 'read', null, nonce, timestamp));
      headers = { 'X-Trail-Signature': sig, 'X-Trail-Nonce': nonce, 'X-Trail-Timestamp': String(timestamp) };
    } catch { /* no usable key — fall back to an unsigned (public-only) read */ }
  }
  const res = await fetch(`/api/v1/devices/${encodeURIComponent(token)}/pings`, headers ? { headers } : undefined);
  if(!res.ok){
    const err: any = new Error('get-pings failed: ' + res.status);
    err.status = res.status;
    throw err;
  }
  return parseDevicePings(await res.json());
}

export async function apiStartSession(token: string, started_at: number, name = null){
  const stored = loadKeypair(token);
  if(!stored) throw new Error('no signing key for ' + token);
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const envelope = canonicalSessionStart(token, 'start', started_at, name, nonce, timestamp);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch('/api/v1/sessions/start', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'X-Trail-Signature': sig },
    body: JSON.stringify({ token, action: 'start', started_at, name, nonce, timestamp }),
  });
  if(!res.ok) throw new Error('session-start failed: ' + res.status);
  return parseSessionStarted(await res.json());
}

export async function apiStopSession(token: string, session_id: number, ended_at: number){
  const stored = loadKeypair(token);
  if(!stored) throw new Error('no signing key for ' + token);
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const envelope = canonicalSessionStop(token, 'stop', session_id, ended_at, nonce, timestamp);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch('/api/v1/sessions/stop', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'X-Trail-Signature': sig },
    body: JSON.stringify({ token, action: 'stop', session_id, ended_at, nonce, timestamp }),
  });
  if(!res.ok) throw new Error('session-stop failed: ' + res.status);
  return parseSessionStopped(await res.json());
}

export async function apiMintShare(token: string, share_scope: any){
  // share_scope: { range, precision, expires }
  const stored = loadKeypair(token);
  if(!stored) throw new Error('no signing key for ' + token);
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const envelope = canonicalShareMint(token, share_scope, nonce, timestamp);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch('/api/v1/shares', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'X-Trail-Signature': sig },
    body: JSON.stringify({ token, share_scope, nonce, timestamp }),
  });
  if(!res.ok){
    const err: any = new Error('share-mint failed: ' + res.status);
    err.status = res.status;
    throw err;
  }
  return parseShareMinted(await res.json());
}

export async function apiGetShareMeta(shareToken: string){
  const res = await fetch(`/api/v1/shares/${encodeURIComponent(shareToken)}`);
  if(res.status === 410) return { gone: true };
  if(res.status === 404) return null;
  if(!res.ok) throw new Error('share-meta failed: ' + res.status);
  return parseShareMeta(await res.json());
}

export async function apiGetSharePings(shareToken: string, since: number | null){
  let url = `/api/v1/shares/${encodeURIComponent(shareToken)}/pings`;
  if(since != null) url += `?since=${since}`;
  const res = await fetch(url);
  if(res.status === 410) return { gone: true };
  if(res.status === 404) return null;
  if(!res.ok) throw new Error('share-pings failed: ' + res.status);
  return parseSharePings(await res.json());
}

export async function apiGetGroupRecent(groupPath: string, since: number | null){
  // groupPath is the URL segment, e.g. 'frg-frg'. since is epoch_ms or null.
  // 404 → null (caller renders empty-cohort state). Other non-OK throws.
  let url = `/api/v1/groups/${encodeURIComponent(groupPath)}/recent`;
  if(since != null) url += `?since=${since}`;
  const res = await fetch(url);
  if(res.status === 404) return null;
  if(!res.ok) throw new Error('group-recent failed: ' + res.status);
  return parseGroupRecent(await res.json());
}

// token format: trail-[<g1>-<g2>-]<a>-<b>-<c>-<slug>
// Each segment maps back through the animal-short table, FALLING BACK to the raw
// segment when it isn't one of the 6 animal shorts. A high-entropy / private
// token's 26-char CSPRNG segments are valid opaque keys (ADR 0011), not shorts,
// so they decode to themselves — making decodeToken the true inverse of tokenFor
// for BOTH animal and high-entropy tokens. The previous `byShort[s]`-only mapping
// returned null for EVERY private token, which stranded boot (→ bounced to the
// picker on reload) and the name header (→ left the "— —" placeholder). The
// body-length gate (4 = label+slug, 6 = group+label+slug) still rejects genuinely
// malformed tokens; the resulting arrays are byte-identical to what the live mint
// (high-entropy.ts) and device history already store, so every render site copes.
export function decodeToken(token: string | null){
  if(typeof token !== 'string') return null;
  const parts = token.split('-');
  if(parts[0] !== 'trail') return null;
  const body = parts.slice(1);
  if(body.length === 4){
    return { animals: body.slice(0,3).map(s => byShort[s] ?? s), group: null };
  }
  if(body.length === 6){
    return {
      group: body.slice(0,2).map(s => byShort[s] ?? s),
      animals: body.slice(2,5).map(s => byShort[s] ?? s),
    };
  }
  return null;
}

// A device minted offline carries a client `Date.now()` created_at; once it
// finally registers the server assigns the authoritative one. Overwrite the
// local value (live device + history) so "By device" self-dedup, which keys on
// created_at, keeps recognising the cohort echo as self.
export function reconcileCreatedAt(token: string, created_at: number){
  if(!Number.isFinite(created_at)) return;
  if(MOCK.device && MOCK.device.token === token){
    MOCK.device.created = new Date(created_at).toISOString();
  }
  const history = loadDeviceHistory();
  const d = history.find((x: any) => x.token === token);
  if(d){ d.created_at = created_at; saveDeviceHistory(history); }
}

// Callback for renderDeviceHeader — set by main.ts boot wiring so that
// api/client.ts does not import ui/sidebar (layer rule: api/ must not import ui/).
let _ensureRenderHeaderCb: (() => void) | null = null;
export function setEnsureRenderHeaderCb(fn: () => void): void { _ensureRenderHeaderCb = fn; }

// Lazily register a device that was minted offline (no server row). Safe to
// call on any device — it no-ops unless the history entry is flagged pending.
// On a network failure it leaves the flag set so a later call retries; on any
// definitive server response it clears the flag (avoids hammering the
// 10/min/IP create limit). If the server issues a different token (a rare
// deterministic-token collision) we adopt it and migrate local state.
//
// Calls are coalesced per token: ensureRegistered fires fire-and-forget from
// device-create and awaited from record-start, and two concurrent POSTs of
// the same token would make the server salt the second into a *different*
// token. The in-flight map collapses overlapping calls onto one promise.
const _registerInFlight = new Map();
export function ensureRegistered(token: string){
  if(!token || !isPendingDevice(token)) return Promise.resolve(token);
  const existing = _registerInFlight.get(token);
  if(existing) return existing;
  const p = _ensureRegisteredOnce(token).finally(() => _registerInFlight.delete(token));
  _registerInFlight.set(token, p);
  return p;
}
async function _ensureRegisteredOnce(token: string){
  const entry = loadDeviceHistory().find((d: any) => d.token === token);
  if(!entry){ setPendingFlag(token, false); return token; }
  let kp = loadKeypair(token);
  try {
    if(!kp){ kp = await generateKeypair(); saveKeypair(token, kp); }
    const res = await fetch('/api/v1/devices', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ animals: entry.animals, group: entry.group || [], pubkey: kp.pub }),
    });
    if(!res.ok) throw new Error('register failed: ' + res.status);
    const out = parseDeviceCreated(await res.json());
    if(out.token === token){
      setPendingFlag(token, false);
      reconcileCreatedAt(token, out.created_at);
      return token;
    }
    // Collision: the deterministic token was taken; adopt the server's.
    saveKeypair(out.token, kp);
    clearKeypair(token);
    retokenHistory(token, { token: out.token, animals: entry.animals, group: entry.group, created_at: out.created_at });
    if(MOCK.device && MOCK.device.token === token){
      MOCK.device.token = out.token;
      MOCK.device.created = new Date(out.created_at).toISOString();
      saveToken(out.token);
      _ensureRenderHeaderCb?.();
    }
    if(loadStoredToken() === token) saveToken(out.token);
    console.warn('[locus] adopted server token after collision', token, '→', out.token);
    return out.token;
  } catch (err) {
    // Still offline / outage — keep pending and try again next time.
    console.warn('[locus] deferred registration still pending', err);
    return token;
  }
}
